import { useEffect, useMemo, useState } from "react"
import styled from "styled-components"

import { EmptyState, ErrorNotice, Metric, Metrics, PanelEyebrow, PrimaryButton } from "@features/portal/PortalPrimitives"
import { serviceRequestsApi } from "@features/portal/serviceRequestsApi"
import { categoryLabel, statusLabel, type ServiceRequest } from "@features/portal/types"
import PortalShell from "../Common/PortalShell"

const ReportGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(300px, 0.6fr);
  gap: 1rem;

  @media (max-width: 850px) {
    grid-template-columns: 1fr;
  }
`

const ReportCard = styled.section`
  padding: clamp(1.35rem, 4vw, 2.5rem);
  border: 1px solid #c4d0da;
  border-radius: 2px;
  background: #ffffff;
  box-shadow: 0 18px 50px rgba(8, 36, 60, 0.05);

  h2 {
    margin: 0;
    color: #102d49;
    font-size: clamp(1.45rem, 3vw, 2.25rem);
    letter-spacing: -0.035em;
  }

  > p {
    margin: 0.8rem 0 0;
    color: #677c8e;
    line-height: 1.65;
  }
`

const Breakdown = styled.div`
  display: grid;
  gap: 1rem;
  margin-top: 1.5rem;
`

const BreakdownRow = styled.div`
  display: grid;
  grid-template-columns: minmax(120px, 0.35fr) minmax(0, 1fr) auto;
  gap: 0.8rem;
  align-items: center;
  color: #334f65;
  font-size: 0.84rem;

  @media (max-width: 520px) {
    grid-template-columns: 1fr auto;

    div {
      grid-column: 1 / -1;
      grid-row: 2;
    }
  }
`

const Bar = styled.div`
  height: 7px;
  background: #e2e9ee;
  overflow: hidden;

  span {
    display: block;
    height: 100%;
    background: linear-gradient(90deg, #1d6da6, #63b9e8);
  }
`

const ExportCard = styled(ReportCard)`
  align-self: start;
  background: #0a223a;
  color: #ffffff;

  h2 {
    color: #ffffff;
  }

  > p {
    color: rgba(255, 255, 255, 0.68);
  }

  button {
    width: 100%;
    margin-top: 1.35rem;
    background: #55aee0;
    border-color: #55aee0;
    color: #071a2e;
  }
`

const ReportList = styled.div`
  display: grid;
  margin-top: 1.25rem;
  border-top: 1px solid #cfdae2;
`

const Filters = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  margin: 1.5rem 0;
  padding: 1rem;
  border: 1px solid #cfdae2;
  background: #f5f8fa;

  label {
    display: grid;
    gap: 0.35rem;
    color: #516a7d;
    font-size: 0.72rem;
    font-weight: 750;
  }

  input,
  select {
    min-width: 0;
    min-height: 44px;
    padding: 0 0.6rem;
    border: 1px solid #b9c8d3;
    border-radius: 2px;
    background: #fff;
    color: #173751;
    font: inherit;
    font-size: 0.78rem;
  }

  @media (max-width: 780px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 470px) {
    grid-template-columns: 1fr;
  }
`

const ButtonRow = styled.div`
  display: grid;
  gap: 0.6rem;

  button {
    margin-top: 0;
  }
`

const ReportLine = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.9rem 0;
  border-bottom: 1px solid #cfdae2;
  color: #335067;
  font-size: 0.86rem;

  strong {
    color: #153b5c;
  }
`

const escapeCsv = (value: unknown) => `"${String(value ?? "").replace(/"/g, '""')}"`

const Reports = () => {
  const [requests, setRequests] = useState<ServiceRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [filters, setFilters] = useState({ from: "", to: "", property: "", status: "" })

  useEffect(() => {
    serviceRequestsApi
      .list()
      .then(setRequests)
      .catch(() => setError("Les données de rapport ne peuvent pas être chargées pour le moment."))
      .finally(() => setLoading(false))
  }, [])

  const properties = useMemo(() => [...new Set(requests.map((request) => request.propertyLabel).filter(Boolean))].sort(), [requests])

  const filteredRequests = useMemo(
    () =>
      requests.filter((request) => {
        const created = new Date(request.createdAt).getTime()
        if (filters.from && created < new Date(`${filters.from}T00:00:00`).getTime()) return false
        if (filters.to && created > new Date(`${filters.to}T23:59:59`).getTime()) return false
        if (filters.property && request.propertyLabel !== filters.property) return false
        if (filters.status && request.status !== filters.status) return false
        return true
      }),
    [filters, requests],
  )

  const stats = useMemo(() => {
    const complete = filteredRequests.filter((request) => request.status === "TERMINEE").length
    const open = filteredRequests.length - complete
    const planned = filteredRequests.filter((request) => request.status === "PLANIFIEE").length
    const propertyCount = new Set(filteredRequests.map((request) => request.propertyLabel).filter(Boolean)).size
    const firstResponseHours = filteredRequests
      .map((request) => {
        const firstAdminMessage = request.messages?.find((message) => message.senderRole === "ADMIN")
        if (!firstAdminMessage) return null
        return Math.max(0, (new Date(firstAdminMessage.createdAt).getTime() - new Date(request.createdAt).getTime()) / 3600000)
      })
      .filter((value): value is number => value !== null)
    const averageFirstResponse = firstResponseHours.length
      ? firstResponseHours.reduce((sum, value) => sum + value, 0) / firstResponseHours.length
      : null
    const categories = Object.entries(categoryLabel).map(([category, label]) => ({
      category,
      label,
      count: filteredRequests.filter((request) => request.category === category).length,
    }))
    return { complete, open, planned, propertyCount, averageFirstResponse, categories }
  }, [filteredRequests])

  const exportCsv = () => {
    const rows = [
      ["Date", "Immeuble", "Unité", "Catégorie", "Priorité", "Statut", "Demandeur", "Description"],
      ...filteredRequests.map((request) => [
        request.createdAt,
        request.propertyLabel,
        request.unit,
        categoryLabel[request.category],
        request.priority,
        statusLabel[request.status],
        request.requesterName,
        request.description,
      ]),
    ]
    const blob = new Blob(["\uFEFF", rows.map((row) => row.map(escapeCsv).join(",")).join("\n")], {
      type: "text/csv;charset=utf-8",
    })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement("a")
    anchor.href = url
    anchor.download = `rapport-demandes-${new Date().toISOString().slice(0, 10)}.csv`
    anchor.click()
    URL.revokeObjectURL(url)
  }

  return (
    <PortalShell title="Rapports" eyebrow="Vue d’ensemble">
      {error && <ErrorNotice role="alert">{error}</ErrorNotice>}
      {loading ? (
        <EmptyState>Préparation du rapport…</EmptyState>
      ) : (
        <ReportGrid>
          <ReportCard>
            <PanelEyebrow>Demandes enregistrées</PanelEyebrow>
            <h2>Portrait opérationnel</h2>
            <p>Un résumé factuel des dossiers visibles dans votre espace.</p>
            <Metrics>
              <Metric>
                <strong>{filteredRequests.length}</strong>
                <span>demandes au total</span>
              </Metric>
              <Metric>
                <strong>{stats.open}</strong>
                <span>en cours</span>
              </Metric>
              <Metric>
                <strong>{stats.planned}</strong>
                <span>planifiées</span>
              </Metric>
              <Metric>
                <strong>{stats.complete}</strong>
                <span>terminées</span>
              </Metric>
            </Metrics>

            <Filters aria-label="Filtres du rapport">
              <label>
                Du
                <input type="date" value={filters.from} onChange={(event) => setFilters((current) => ({ ...current, from: event.target.value }))} />
              </label>
              <label>
                Au
                <input type="date" value={filters.to} onChange={(event) => setFilters((current) => ({ ...current, to: event.target.value }))} />
              </label>
              <label>
                Immeuble
                <select value={filters.property} onChange={(event) => setFilters((current) => ({ ...current, property: event.target.value }))}>
                  <option value="">Tous</option>
                  {properties.map((property) => (
                    <option key={property} value={property}>
                      {property}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Statut
                <select value={filters.status} onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}>
                  <option value="">Tous</option>
                  {Object.entries(statusLabel).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
            </Filters>

            {filteredRequests.length ? (
              <Breakdown aria-label="Répartition par catégorie">
                {stats.categories.map((item) => (
                  <BreakdownRow key={item.category}>
                    <span>{item.label}</span>
                    <Bar>
                      <span style={{ width: `${(item.count / filteredRequests.length) * 100}%` }} />
                    </Bar>
                    <strong>{item.count}</strong>
                  </BreakdownRow>
                ))}
              </Breakdown>
            ) : (
              <EmptyState>Le rapport se remplira à mesure que des demandes seront enregistrées.</EmptyState>
            )}
          </ReportCard>

          <ExportCard>
            <PanelEyebrow>Export</PanelEyebrow>
            <h2>Conserver une copie</h2>
            <p>Le fichier CSV comprend les demandes, leur statut et les renseignements de suivi actuels.</p>
            <ButtonRow>
              <PrimaryButton onClick={exportCsv} disabled={!filteredRequests.length}>
                Télécharger le CSV
              </PrimaryButton>
              <PrimaryButton onClick={() => window.print()} disabled={!filteredRequests.length}>
                Imprimer le rapport
              </PrimaryButton>
            </ButtonRow>
          </ExportCard>

          <ReportCard>
            <PanelEyebrow>Portefeuille visible</PanelEyebrow>
            <h2>Immeubles suivis</h2>
            <ReportList>
              <ReportLine>
                <span>Immeubles avec une demande enregistrée</span>
                <strong>{stats.propertyCount}</strong>
              </ReportLine>
              <ReportLine>
                <span>Dossiers actuellement planifiés</span>
                <strong>{stats.planned}</strong>
              </ReportLine>
              <ReportLine>
                <span>Dossiers terminés</span>
                <strong>{stats.complete}</strong>
              </ReportLine>
              <ReportLine>
                <span>Première réponse moyenne*</span>
                <strong>{stats.averageFirstResponse === null ? "—" : `${stats.averageFirstResponse.toFixed(1)} h`}</strong>
              </ReportLine>
            </ReportList>
            <p>*Calculée à partir du premier message de l’équipe enregistré dans Espace TB.</p>
          </ReportCard>
        </ReportGrid>
      )}
    </PortalShell>
  )
}

export default Reports
